import type { schema } from '../../../schema';
import type { FieldsArray } from './fields-array';

export type RawFormSchema = (typeof schema.forms)[number]['schema'];

// getFormSchema inserts a title placeholder into each layout tuple.
type AddTitlePlaceholder<T> = T extends readonly [infer N, infer C]
    ? C extends string
        ? [N, null, C]
        : C extends readonly unknown[]
        ? [N, null, { [K in keyof C]: AddTitlePlaceholder<C[K]> }]
        : never
    : never;

export type FormSchema = Omit<RawFormSchema, 'fields' | 'layout'> & {
    fields: Readonly<FieldsArray<RawFormSchema['fields'][number]>>;
    layout: Array<AddTitlePlaceholder<RawFormSchema['layout'][number]>>;
};

// Helper type to recursively extract all field types from a schema
// This searches through top-level fields and nested fields (like in alarms)
type ExtractAllFields<T> = T extends readonly (infer U)[]
    ? U extends { fields: readonly unknown[] }
        ? U | ExtractAllFields<U['fields']>
        : U extends { alarms: readonly unknown[] }
        ? U | ExtractAllFields<U['alarms']>
        : U
    : never;

export type FormFields = FormSchema['fields'];
export type FormLayout = FormSchema['layout'];

// A helper type that represents a single field object.
// Uses deep search to extract all fields including nested ones from alarms.
// For example: { name: 'name', type: 'char' } | { name: 'deviceName', type: 'char' } |
//              { name: 'title', type: 'char' } (from alarm fields) | ...
export type FormField = ExtractAllFields<FormFields>;

type FieldsWithDefault = Extract<FormField, { name: string; default: unknown }>;

export type FieldDefaultsByName = {
    [F in FieldsWithDefault as F['name']]: F['default'];
};

type BaseTranslatedFormField = {
    label?: string;
    helpText?: string;
    placeholder?: string;
    options?: Array<[Extract<FormField, { options: unknown }>['options'][number], string]>;
};

type BaseNotificationConfigAlarm = Extract<
    FormField,
    { name: 'notificationConfig' }
>['alarms'][number];

type BaseNotificationConfigAlarmField = BaseNotificationConfigAlarm['fields'][number];

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type TranslatedNotificationConfigAlarmField = DistributiveOmit<
    BaseNotificationConfigAlarmField,
    'options'
> & {
    label: string;
    helpText: string;
    options?: Array<
        [Extract<BaseNotificationConfigAlarmField, { options: unknown }>['options'][number], string]
    >;
};

type TranslatedNotificationConfigAlarm = DistributiveOmit<BaseNotificationConfigAlarm, 'fields'> & {
    title: string;
    fields: TranslatedNotificationConfigAlarmField[];
};

export type TranslatedFormField = FormField extends infer F
    ? F extends { name: 'notificationConfig' }
        ? DistributiveOmit<F, 'alarms' | 'options'> &
              BaseTranslatedFormField & { alarms: TranslatedNotificationConfigAlarm[] }
        : DistributiveOmit<F, 'options'> & BaseTranslatedFormField
    : never;

type AddTitle<T> = T extends readonly [infer N, null, infer C] // [name, null, child].
    ? C extends string
        ? [N, string, C]
        : C extends readonly unknown[] // C is an array (possibly nested).
        ? [N, string, { [K in keyof C]: AddTitle<C[K]> }]
        : never
    : never;

export type TranslatedFormLayout = AddTitle<FormLayout[number]>;

export type TranslatedFormSchema = Omit<FormSchema, 'fields' | 'layout'> & {
    fields: Readonly<FieldsArray<TranslatedFormField>>;
    layout: Readonly<Array<TranslatedFormLayout>>;
};

// Extract all possible field type values from both FormField and TranslatedFormField
export type FormFieldTypes = FormFields[number]['type'];

export type FormFieldNames = FormField['name'];

// Helper type to get all fields of a specific type from both schema and translated fields
export type FieldOfType<T extends FormFieldTypes> =
    | Extract<FormField, { type: T }>
    | Extract<TranslatedFormField, { type: T }>;

export type FieldOfName<T extends FormFieldNames> =
    | Extract<FormField, { name: T }>
    | Extract<TranslatedFormField, { name: T }>;

// Helper to collect all keys from a union type
type UnionKeys<T> = T extends unknown ? keyof T : never;

// Helper to get the value type for a specific key from a union
// If the key doesn't exist in a union member, it returns undefined
type GetUnionValue<U, K extends PropertyKey> = U extends unknown
    ? K extends keyof U
        ? U[K]
        : undefined
    : never;

// Helper to merge all union members into a single object with all possible properties
// Properties that exist in some but not all members will have 'undefined' in their type
export type MergedFieldOfType<T extends FormFieldTypes> = {
    [K in UnionKeys<FieldOfType<T>>]: GetUnionValue<FieldOfType<T>, K>;
};

// Merged field type for all form field types to get a comprehensive field definition.
export type MergedFormField = MergedFieldOfType<FormFieldTypes>;

export type SchemaIntegerField = FieldOfType<'integer'>;
export type SchemaFloatField = FieldOfType<'float'>;
export type SchemaBooleanField = FieldOfType<'boolean'>;

type SchemaUnitConvertibleField = Extract<FormField, { unitConversion: string }>;
type TranslatedUnitConvertibleField = Extract<TranslatedFormField, { unitConversion: string }>;

export type UnitConvertibleField = SchemaUnitConvertibleField | TranslatedUnitConvertibleField;

export function hasUnitConversion(
    field: FormField | TranslatedFormField
): field is UnitConvertibleField {
    return typeof (field as { unitConversion?: unknown }).unitConversion === 'string';
}
